// Admin — proof-of-life log of GymSales lookups (playbook + re-match).
// Click a row to open the underlying call.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ScrollText, CheckCircle2, XCircle, Loader2, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MatchLogTable = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [matched, setMatched] = useState('all');
  const [days, setDays] = useState(0);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: '100' });
    if (matched !== 'all') p.set('matched', matched);
    if (days) p.set('days', String(days));
    axios.get(`${API}/admin/gymsales/match-log?${p.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [] }))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [matched, days]);

  const [exporting, setExporting] = React.useState(false);
  const [creating, setCreating] = React.useState(null);

  const createContact = async (callId, idx) => {
    setCreating(idx);
    try {
      const r = await axios.post(`${API}/admin/gymsales/create-contact`, { call_id: callId });
      toast.success(`Created ${r.data.person_name} in GymSales — deal now tracked`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    }
    setCreating(null);
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (matched !== 'all') p.set('matched', matched);
      if (days) p.set('days', String(days));
      const r = await axios.get(`${API}/admin/gymsales/match-log.csv?${p.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gymsales-match-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* toastless — button state resets */ }
    setExporting(false);
  };

  const Pill = ({ active, onClick, children, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${active ? 'bg-teal-500 text-slate-950' : 'bg-white/5 text-white/50 hover:text-white'}`}>
      {children}
    </button>
  );

  const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-4" data-testid="gymsales-match-log">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-teal-300" /> Match log — recent GymSales lookups
        </h3>
        <div className="flex items-center gap-3">
          {data && <span className="text-white/40 text-[11px]">{data.matched} matched of last {data.total}</span>}
          <button onClick={exportCsv} disabled={exporting}
            className="text-[11px] font-bold text-teal-300 hover:text-teal-200 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="match-log-export-btn">
            <Download className="w-3 h-3" /> {exporting ? 'Exporting…' : 'CSV'}
          </button>
          <button onClick={load} className="text-white/40 hover:text-white" data-testid="match-log-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="match-log-filters">
        <Pill active={matched === 'all'} onClick={() => setMatched('all')} testid="match-log-filter-all">All</Pill>
        <Pill active={matched === 'true'} onClick={() => setMatched('true')} testid="match-log-filter-matched">Matched</Pill>
        <Pill active={matched === 'false'} onClick={() => setMatched('false')} testid="match-log-filter-unmatched">Unmatched</Pill>
        <span className="w-px h-4 bg-white/10 mx-1" />
        <Pill active={days === 1} onClick={() => setDays(days === 1 ? 0 : 1)} testid="match-log-filter-24h">24h</Pill>
        <Pill active={days === 7} onClick={() => setDays(days === 7 ? 0 : 7)} testid="match-log-filter-7d">7d</Pill>
        <Pill active={days === 30} onClick={() => setDays(days === 30 ? 0 : 30)} testid="match-log-filter-30d">30d</Pill>
      </div>
      {data === null ? (
        <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
      ) : (data.rows || []).length === 0 ? (
        <p className="text-white/40 text-xs italic">No lookups match these filters — entries appear whenever a playbook or re-match queries GymSales.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 text-[10px] uppercase tracking-widest text-left">
                <th className="py-1.5 pr-3">When</th><th className="py-1.5 pr-3">Phone</th>
                <th className="py-1.5 pr-3">Result</th><th className="py-1.5 pr-3">GymSales contact</th>
                <th className="py-1.5 pr-3">Source</th><th className="py-1.5" />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.call_id}-${r.ts}-${i}`}
                  onClick={() => r.call_id && navigate(`/calls/${r.call_id}`)}
                  className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
                  data-testid={`match-log-row-${i}`}
                  title="Open the call / revivable deal">
                  <td className="py-2 pr-3 text-white/60 whitespace-nowrap">{fmt(r.ts)}</td>
                  <td className="py-2 pr-3 text-white/80 tabular-nums">
                    {r.phone || '—'}
                    {(r.variants || []).length > 0 && (
                      <span className="block text-[10px] text-white/35"
                        title={`Formats tried: ${r.variants.join(', ')}`}
                        data-testid={`match-log-variants-${i}`}>
                        tried {r.variants.length} formats: {r.variants.join(' · ')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {r.matched ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> {r.reason === 'created' ? 'Created' : 'Matched'}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-white/40 text-xs"><XCircle className="w-3.5 h-3.5" /> {r.reason === 'lookup_failed' ? 'Lookup failed' : 'No match'}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-white/70">{r.person_name || '—'}</td>
                  <td className="py-2 pr-3 text-white/40 text-xs">{r.source}</td>
                  <td className="py-2 text-right">
                    {!r.matched && r.phone && r.call_id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); createContact(r.call_id, i); }}
                        disabled={creating === i}
                        className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-teal-500/15 text-teal-300 hover:bg-teal-500/30 disabled:opacity-50 whitespace-nowrap"
                        data-testid={`match-log-create-${i}`}>
                        {creating === i ? 'Creating…' : '+ Create in GymSales'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
